import { useTranslation } from 'react-i18next';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Box, Stack, styled, Typography } from '@mui/material';
import { Fragment, memo, useCallback, useMemo } from 'react';
import { Link } from 'react-router';
import type ViewCategoryStore from '../../AppData/_stores/ViewCategoryStore';
import { getCanonicalLocale, getI18nextLocale, joinSeoPath } from '../../Common/SEO/SeoLinks';
import { getSeoProductsName } from '../../Common/SEO/StructuredData';
import useProductCatalogFiltering from '../hooks/useProductCatalogFiltering';
import ProductCatalogHead from './ProductCatalogHead';

const CatalogHeaderShell = styled(Box)(({ theme }) => ({
  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  padding: theme.spacing(0, 0, 3),

  [theme.breakpoints.down('sm')]: {
    marginBottom: 0,
    paddingBottom: theme.spacing(1.5),
  },
}));

const HeaderText = styled(Stack)(({ theme }) => ({
  maxWidth: 760,
  paddingTop: theme.spacing(1),
  position: 'relative',
  zIndex: 1,
}));

const CatalogTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.secondary.main,
  fontSize: '4.25rem',
  lineHeight: 0.92,
  position: 'relative',
  zIndex: 1,

  [theme.breakpoints.down('sm')]: {
    fontSize: '2.45rem',
    lineHeight: 1,
  },
}));

const BreadcrumbNav = styled('nav')(({ theme }) => ({
  alignItems: 'center',
  color: 'rgba(0, 0, 0, 0.58)',
  display: 'flex',
  flexWrap: 'wrap',
  fontFamily: 'Poppins, Roboto, sans-serif',
  fontSize: 12,
  fontWeight: 600,
  gap: theme.spacing(0.75),
  letterSpacing: 0,
  lineHeight: 1.4,
  maxWidth: '100%',
  position: 'relative',
  zIndex: 2,

  '& a': {
    color: 'inherit',
    textDecoration: 'none',
    textUnderlineOffset: 4,
    transition: 'color 160ms ease, opacity 160ms ease',
  },

  '& a:hover, & a:focus-visible': {
    color: 'rgba(0, 0, 0, 0.86)',
    outline: 'none',
    textDecoration: 'underline',
  },

  '& .product-catalog-breadcrumb-separator': {
    color: 'rgba(0, 0, 0, 0.28)',
    userSelect: 'none',
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: 11,
    gap: theme.spacing(0.5),
  },
}));

const CatalogDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.secondary.light,
  maxWidth: 640,

  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

interface ProductCatalogBreadcrumbItem {
  label: string
  to: string
}

const getLocalizedCategorySlug = (category: ViewCategoryStore, locale: ReturnType<typeof getCanonicalLocale>) => {
  return category.get('url_slugs').get(locale) ?? category.get('url_slug');
}

const SamplePackLinkText = styled('span')({
  borderBottom: '1px solid transparent',
  transition: 'border-color 160ms ease',
});

const SamplePackLinkIcon = styled(ArrowForwardRoundedIcon)({
  fontSize: '1rem',
  transition: 'transform 160ms ease',
});

const SamplePackLink = styled(Link)(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.accent.main,
  display: 'inline-flex',
  fontSize: '0.95rem',
  fontWeight: 700,
  gap: theme.spacing(0.35),
  lineHeight: 1.35,
  marginLeft: theme.spacing(1.25),
  textDecoration: 'none',
  whiteSpace: 'nowrap',

  '&:hover': {
    color: theme.palette.accent.main,
    textDecoration: 'none',

    [`${SamplePackLinkText}`]: {
      borderBottomColor: 'currentColor',
    },

    [`${SamplePackLinkIcon}`]: {
      transform: 'translateX(2px)',
    },
  },

  '&:focus-visible': {
    outline: '2px solid ' + theme.palette.accent.main,
    outlineOffset: 3,
  },

  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const MobileSamplePackLink = styled(SamplePackLink)(({ theme }) => ({
  display: 'none',
  marginLeft: 0,
  width: 'fit-content',

  [theme.breakpoints.down('sm')]: {
    display: 'inline-flex',
  },
}));

function ProductCatalogHeader() {
  const [t] = useTranslation('products')
  const [tR, routesI18n] = useTranslation('routes')
  const {
    category1,
    category2,
    selectedCollection,
  } = useProductCatalogFiltering();
  const currentLocale = getCanonicalLocale(routesI18n.resolvedLanguage ?? routesI18n.language);
  const selectedCategory = category2 ?? category1;

  const title = selectedCollection !== undefined ? selectedCollection.title(t) : selectedCategory !== undefined ? selectedCategory.get('name') : t('All Products');
  const description = selectedCollection !== undefined
    ? selectedCollection.description(t)
    : selectedCategory !== undefined
    ? selectedCategory.get('description')
    : t('Browse customizable products for print-on-demand apparel, accessories, home decor, and studio staples.');
  const breadcrumbItems = useMemo<ProductCatalogBreadcrumbItem[]>(() => {
    const fixedRoutes = routesI18n.getFixedT(getI18nextLocale(currentLocale), 'routes');
    const productBasePath = fixedRoutes('/products');
    const productsBreadcrumb = {
      label: getSeoProductsName(currentLocale),
      to: productBasePath,
    };

    if (selectedCollection !== undefined) {
      return [productsBreadcrumb];
    }

    const items: ProductCatalogBreadcrumbItem[] = [productsBreadcrumb];

    if (selectedCategory === undefined) {
      return items;
    }

    const categoryPath: string[] = [];

    [category1, category2].forEach(category => {
      if (category === undefined || category.get('id') === selectedCategory.get('id')) return;

      categoryPath.push(getLocalizedCategorySlug(category, currentLocale));
      items.push({
        label: category.get('name'),
        to: joinSeoPath(productBasePath, ...categoryPath),
      });
    });

    return items;
  }, [category1, category2, currentLocale, routesI18n, selectedCategory, selectedCollection]);
  const breadcrumbAriaLabel = useMemo(() => {
    return breadcrumbItems.map(item => item.label).join(' / ');
  }, [breadcrumbItems]);
  const renderBreadcrumbItem = useCallback((item: ProductCatalogBreadcrumbItem, index: number) => {
    return <Fragment key={item.to}>
      {index > 0 ? <span className="product-catalog-breadcrumb-separator" aria-hidden="true">/</span> : null}
      <Link to={item.to}>{item.label}</Link>
    </Fragment>;
  }, []);

  return <>
    <ProductCatalogHead title={title} description={description} />
    <CatalogHeaderShell component="header">
      <HeaderText spacing={2}>
        <Stack spacing={0.5}>
          {breadcrumbItems.length > 0 ? <BreadcrumbNav aria-label={breadcrumbAriaLabel}>
            {breadcrumbItems.map(renderBreadcrumbItem)}
          </BreadcrumbNav> : null}
          <CatalogTitle variant="h1">{title}</CatalogTitle>
        </Stack>
        <MobileSamplePackLink to={tR('/info/sample-packs')}>
          <SamplePackLinkText>{t('Order sample packs')}</SamplePackLinkText>
          <SamplePackLinkIcon />
        </MobileSamplePackLink>
        <CatalogDescription variant="body1">
          {description}
          <SamplePackLink to={tR('/info/sample-packs')}>
            <SamplePackLinkText>
              {t('Order sample packs')}
            </SamplePackLinkText>
            <SamplePackLinkIcon />
          </SamplePackLink>
        </CatalogDescription>
      </HeaderText>
    </CatalogHeaderShell>
  </>
}

export default memo(ProductCatalogHeader);
